/**
 * 他车关系：每个共有障碍物在两侧的最近距离、是否碰到、位于自车哪一侧、先过还是后过。
 * 数据来自 facts.interaction（最近距离）与 detail.movement（左右、先后）。
 * 默认只列「有变化」的；可切换看全部。
 */
import { Switch, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMemo, useState } from 'react';

import { buildEncounters, positionShiftHint, sortEncountersByProximity, type EncounterRow } from '../format';

const fmt = (v: number | null | undefined): string => (v == null ? '—' : `${v.toFixed(2)}m`);

interface Props {
  facts: Record<string, unknown> | null | undefined;
  movement: Record<string, unknown> | null | undefined;
  /** 自车位置差最大值（米），用来判断距离差是不是主要由自车位置差造成 */
  posMax?: number | null;
}

export default function EncounterTable({ facts, movement, posMax }: Props) {
  const [showAll, setShowAll] = useState(false);
  const rows = useMemo(
    () => buildEncounters(facts as never, movement as never),
    [facts, movement],
  );
  const hint = useMemo(() => positionShiftHint(rows, posMax), [rows, posMax]);
  const shown = useMemo(() => sortEncountersByProximity(showAll ? rows : rows.filter((r) => r.changed)), [rows, showAll]);

  const columns: ColumnsType<EncounterRow> = [
    { title: '障碍物', dataIndex: 'id', width: 120, render: (_: unknown, r) => `${r.typeName} #${r.id}` },
    {
      title: '最近距离（基线 → 当前）', key: 'min', width: 190,
      render: (_: unknown, r) => (
        <span>
          {fmt(r.minBase)} → {fmt(r.minCur)}
          {r.contactBase !== r.contactCur ? <Tag color="red" bordered={false} style={{ marginLeft: 4 }}>
            {r.contactCur ? '当前碰到' : '基线碰到'}
          </Tag> : null}
        </span>
      ),
    },
    { title: '差值', dataIndex: 'minDiff', width: 80, render: (v: number | null) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(2)}m`) },
    { title: '近距时长（秒）', key: 'dur', width: 120, render: (_: unknown, r) => `${r.closeDurBase.toFixed(1)} → ${r.closeDurCur.toFixed(1)}` },
    { title: '所在侧', dataIndex: 'side', width: 100, render: (v: string | null) => v ?? '—' },
    { title: '先过 / 后过', dataIndex: 'passOrder', width: 170, render: (v: string | null) => v ?? '—' },
    {
      title: '备注', key: 'note', width: 110,
      render: (_: unknown, r) => (r.onlyOneSide
        ? <Tag color="warning" bordered={false}>仅{r.onlyOneSide === 'base' ? '基线' : '当前'}侧出现</Tag>
        : null),
    },
  ];

  if (rows.length === 0) {
    return <span className="bc-muted">该场景没有两侧共有的障碍物可比较。</span>;
  }
  return (
    <div>
      <div style={{ marginBottom: 6, fontSize: 12, color: '#6b6f76' }}>
        共 {rows.length} 个共有障碍物，其中 {rows.filter((r) => r.changed).length} 个有变化，按最近距离由近到远排列
        <span style={{ marginLeft: 12 }}>
          显示全部 <Switch size="small" checked={showAll} onChange={setShowAll} />
        </span>
      </div>
      {hint ? <div className="bc-hint">{hint}</div> : null}
      <Table<EncounterRow>
        size="small" rowKey="id" columns={columns} dataSource={shown}
        pagination={shown.length > 10 ? { pageSize: 10, size: 'small' } : false}
        locale={{ emptyText: '没有发生变化的障碍物' }}
      />
    </div>
  );
}
